import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { features, routes } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { HolidayGroup } from './models/statutory-holidays.model';
import { StatutoryHolidaysService } from './services/statutory-holidays.service';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';

@Component({
  selector: 'app-statutory-holidays',
  templateUrl: './statutory-holidays.component.html',
  styleUrls: ['./statutory-holidays.component.scss']
})
export class StatutoryHolidaysComponent extends SecurityProtectedBase implements OnInit {
  gridId: string = 'statutory-holidays';
  refreshView: boolean = true;
  public routes: typeof routes = routes;
  public columns: any[] = [
    {
      field: 'name',
      title: this.translate.instant('StatutoryHoliday-Name'),
    },
    {
      field: 'country',
      subField: 'name',
      title: this.translate.instant('StatutoryHoliday-Country'),
      dataType: 'Lookup',
      lookupCode: 'Country'
    }
  ];
  filterCategories: any[] = [
    {field: 'name', title: this.translate.instant('StatutoryHoliday-Name'), dataType: 'String'},
    {field: 'Country.Name', title: this.translate.instant('StatutoryHoliday-Country'), dataType: 'String'},
  ];
  sortableColumns: any[] = [
    { field: "name", sortValue: "name" },
    { field: "country", sortValue: "Country.Name" },
  ];
  public bindingType: String = 'array';
  public gridDataResult: GridDataResult;
  public selectedPositions: any[] = [];
  isLoading: boolean;
  pageSize: number = 20;
  skip: number = 0;
  filterString: string;
  getRequest: any;
  public searchValue: string;
  clearSelectedItems: boolean = false;
  sortString: string;
  searchFilterString: string;

  constructor(
    featureService: FeatureService,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
    private holidayService: StatutoryHolidaysService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super(featureService, features.TIME_OFF)
  }

  ngOnInit(): void {
    this.getHolidayGroups();
  }

  getHolidayGroups(): void {
    this.isLoading = true;

    this.getRequest = this.holidayService.getHolidayGroups(this.pageSize, this.skip, this.sortString, this.filterString)
    .pipe(
      finalize(() => {
        this.isLoading = false;
      })
    )
    .subscribe(
      res => {
        this.gridDataResult = {
          data: res.data,
          total: res.total,
        };
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.getHolidayGroups();
  }

  deleteAllSelected(itemsToDelete: string[]) {
    const observables = itemsToDelete.map(selectedPosition => defer(() => this.holidayService.deleteHolidayGroup(selectedPosition)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
        finalize(() => this.overlayService.hide())
    )
    .subscribe(
        (res) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.getHolidayGroups();
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
        },
        err => {
            this.snackbarService.openSnackBar(err, 'clear', 'warn');
        }
    );
  }

  navigateToWorkRotation(holidayGroup?: HolidayGroup) {
    if(holidayGroup){
      this.router.navigate([`${routes.SITE_SETTINGS}${routes.STATUTORY_HOLIDAYS}${routes.EDITOR}/${holidayGroup?.id}`], {relativeTo: this.route});
    }
    else {
      this.router.navigate([`${routes.SITE_SETTINGS}${routes.STATUTORY_HOLIDAYS}${routes.EDITOR}`], {relativeTo: this.route});
    }
  }

  gridStateChange(gridState: GridState) {
    if(this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if(this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if(this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if(this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }
    
    if(this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if(this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
          if (variant) {
              if (this.searchFilterString.length > 2) {
                  this.searchFilterString += ' AND ';
              }
              this.searchFilterString += `(Name like "${variant}")`;
          }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getHolidayGroups();
  }
}
